<script lang="ts">
	import { CardSwiper } from '@infinitro/card-swiper';
	import { GameCategory, getGames, type GameInfo } from '@infinitro/catalog-min';
	import { cfgToThemeCdnPath } from '@infinitro/config';
	import { t, Verbiage } from '@infinitro/content';
	import { jackpotAmounts, startMegajackpotPolling } from '@infinitro/global/jackpot';
	import { onMount } from 'svelte';
	import JackpotGameCard from './jackpot-game-card.svelte';

	export let onViewAllClick: (() => void) | undefined = undefined;

	let jackpotGames: GameInfo[] = [];
	let mounted = false;

	onMount(() => {
		const stopMegajackpotPolling = startMegajackpotPolling();

		void (async () => {
			try {
				// Fetch games for the jackpot swiper - using popular games as jackpot games
				[jackpotGames] = await getGames(1, 24, undefined, GameCategory.Popular);
			} catch (error) {
				console.warn('Failed to fetch jackpot games:', error);
				jackpotGames = [];
			}
			mounted = true;
		})();

		return stopMegajackpotPolling;
	});

	const scrollToTop = () => {
		document
			.querySelector<HTMLElement>('.innerpage')
			?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
	};

	const handleViewAllClick = () => {
		if (onViewAllClick) {
			onViewAllClick();
			return;
		}

		scrollToTop();
	};

	// CDN paths for the background images and logo
	$: bgImageUrl = cfgToThemeCdnPath('/home-page/jackpot-bg-d.png');
	$: bgImageUrlMobile = cfgToThemeCdnPath('/home-page/jackpot-bg-m.png');
	$: logoUrl = cfgToThemeCdnPath('/home-page/turbo-jackpot-logo.png');
</script>

{#if mounted}
	<div class="jackpot-drop-container">
		<!-- Background layers with animation -->
		<div class="background-layer">
			<!-- Desktop background -->
			<img class="background-image bg-primary" src={bgImageUrl} alt="" />
			<!-- Mobile background -->
			<img class="background-image bg-mobile" src={bgImageUrlMobile} alt="" />
			<div class="background-overlay"></div>
		</div>

		<!-- Content container -->
		<div class="content-wrapper">
			<!-- Logo section -->
			<div class="logo-section">
				<img src={logoUrl} alt={$t(Verbiage.turbo_jackpot_logo_alt)} class="jackpot-logo" />
			</div>

			<!-- Mega Jackpot -->
			<div class="jackpot-item main">
				<div class="jackpot-label">{$t(Verbiage.jackpot_mega)}</div>
				<div class="jackpot-value main-value gradient-text">{jackpotAmounts.mega}</div>
			</div>

			<!-- Jackpot values section -->
			<div class="jackpots-section">
				<!-- Major Jackpot -->
				<div class="jackpot-item">
					<div class="jackpot-label">{$t(Verbiage.jackpot_major)}</div>
					<div class="jackpot-value">{jackpotAmounts.major}</div>
				</div>

				<!-- Minor Jackpot -->
				<div class="jackpot-item">
					<div class="jackpot-label">{$t(Verbiage.jackpot_minor)}</div>
					<div class="jackpot-value">{jackpotAmounts.minor}</div>
				</div>

				<!-- Mini Jackpot -->
				<div class="jackpot-item">
					<div class="jackpot-label">{$t(Verbiage.jackpot_mini)}</div>
					<div class="jackpot-value">{jackpotAmounts.mini}</div>
				</div>
			</div>

			<!-- Available on selected games text -->
			<div class="available-text">{$t(Verbiage.available_on_all_casino_games)}</div>

			<!-- Games swiper -->
			<div class="jackpot-swiper-section">
				<CardSwiper
					data={jackpotGames}
					title=""
					url="/casino/"
					icon={null}
					customSlidesPerView={{
						200: 3.5,
						480: 6,
						720: 8,
						1024: 11.5,
						1280: 11.5
					}}
					spaceBetween={12}
					irregular={true}
					noCardLink={true}
					touchRatio={1.15}
					freeModeMomentumRatio={1.1}
					freeModeMomentumVelocityRatio={1.1}
					customStyleSwiperContainer="height: 130px; margin-top: 0px; padding-top: 8px; padding-bottom: 8px;"
					CustomCard={JackpotGameCard}
				/>
			</div>

			<!-- View all link -->
			<button class="view-all-link" on:click={handleViewAllClick}>
				{$t(Verbiage.viewAll)}
				{$t(Verbiage.games)}
				→
			</button>
		</div>
	</div>
{/if}

<style>
	.jackpot-drop-container {
		position: relative;
		margin-top: 1rem;
		margin-bottom: 1rem;
		width: 100%;
		border-radius: 16px;
		min-height: 458px;
		margin-top: 80px;
}

	.background-layer {
		position: absolute;
		inset: 0px;
		overflow: hidden;
		border-radius: 16px;
}

	.background-image {
		inset: 0px;
		height: 100%;
		width: 100%;
		-o-object-fit: cover;
		   object-fit: cover;
}

	.bg-primary {
		display: none;
}

	@media (min-width: 1024px) {

		.bg-primary {
				display: block;
		}
}

	.bg-mobile {
		display: block;
}

	@media (min-width: 1024px) {

		.bg-mobile {
				display: none;
		}
}

	.background-overlay {
		position: absolute;
		inset: 0px;
		background: rgba(0, 0, 0, 0.4);
}

	.content-wrapper {
		position: relative;
		z-index: 10;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding-top: 5rem;
		padding-bottom: 1.5rem;
}

	.logo-section {
		display: flex;
		justify-content: center;
		position: absolute;
		left: 50%;
		transform: translate(-50%, -50%);
		top: 0%;
}

	.jackpot-logo {
		height: 140px;
		width: auto;
		-o-object-fit: contain;
		   object-fit: contain;
}

	.jackpots-section {
		margin-bottom: 3rem;
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 1rem;
}

	@media (min-width: 1024px) {

		.jackpots-section {
				flex-direction: row;
				align-items: flex-start;
				gap: 4rem;
		}
}

	.jackpot-item {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		min-width: 140px;
}

	.jackpot-label {
		margin-bottom: 0.5rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		opacity: 0.6;
		color: var(--component-home-page-turbo-jackpot-drop-jackpot-label);
}

	.jackpot-item.main {
		margin-bottom: 18px;
}

	@media (min-width: 1024px) {

		.jackpot-item.main {
				margin-bottom: 24px;
		}
}

	.jackpot-item.main .jackpot-label {
		opacity: 1;
}

	.jackpot-value {
		font-size: 1.875rem;
		line-height: 2.25rem;
		font-weight: 700;
}

	@media (min-width: 1024px) {

		.jackpot-value {
				font-size: 2.25rem;
				line-height: 2.5rem;
		}
}

	.jackpot-value {
		color: var(--component-home-page-turbo-jackpot-drop-jackpot-value);
		filter: drop-shadow(
			0 0 28.2325px
				color-mix(
					in srgb,
					var(--component-home-page-turbo-jackpot-drop-jackpot-value) 50%,
					transparent
				)
		);
	}

	.main-value {
		font-size: 3rem;
		line-height: 1;
}

	@media (min-width: 1024px) {

		.main-value {
				font-size: 3.75rem;
				line-height: 1;
		}
}

	.main-value {
		white-space: nowrap;
		filter: drop-shadow(
			0 0 28.2325px
				color-mix(
					in srgb,
					var(--component-home-page-turbo-jackpot-drop-main-jackpot-gradient-to) 50%,
					transparent
				)
		);
	}

	.gradient-text {
		background: linear-gradient(
			180deg,
			var(--component-home-page-turbo-jackpot-drop-main-jackpot-gradient-from) 49%,
			var(--component-home-page-turbo-jackpot-drop-main-jackpot-gradient-to) 43%
		);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
	}

	.available-text {
		margin-bottom: 1rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 500;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		opacity: 0.6;
		color: var(--component-home-page-turbo-jackpot-drop-jackpot-label);
		letter-spacing: 4px;
}

	.jackpot-swiper-section {
		margin-top: 1.5rem;
		margin-bottom: 1.5rem;
}

	@media (min-width: 1024px) {

		.jackpot-swiper-section {
				margin-bottom: 3rem;
		}
}

	.jackpot-swiper-section {
		overflow: hidden;
		width: 100%;
	}

	.jackpot-swiper-section :global(.card-swiper-container) {
		-webkit-mask-image: none !important;
		mask-image: none !important;
	}

	.view-all-link {
		cursor: pointer;
		border-width: 0px;
		background-color: transparent;
		text-decoration-line: underline;
		text-underline-offset: 2px;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		color: var(--component-home-page-turbo-jackpot-drop-view-all-link);
}

	.view-all-link:hover {
		color: var(--component-home-page-turbo-jackpot-drop-view-all-link-hover);
	}

	@media (max-width: 640px), (max-width: 1023px) {
		.jackpots-section {
				flex-direction: column;
				gap: 1.5rem;
		}

		.available-text {
			font-size: 10px;
		}
		.jackpot-item {
				width: 100%;
		}

		.jackpot-value {
				font-size: 1.5rem;
				line-height: 2rem;
		}

		.main-value {
			font-size: 34px;
		}

		.content-wrapper {
				padding-top: 3rem;
		}
	}
</style>
